import { useAnalyticsTracking } from '@/analytics/snowplow/AnalyticsTrackingContext';
import { useEmitUiEvent } from '@/analytics/snowplow/useEmitUIEvent';
import { useAccessControl } from '@/hooks';
import type { Consultation } from '@/hooks/patients/usePatientConsultations';
import { useAppStore } from '@/state-management';
import { UserRole } from '@/types';
import type { SelectChangeEvent } from '@mui/material';
import { Box, MenuItem, Select, TextField, Typography } from '@mui/material';
import { useEffect } from 'react';

type ConsultNotesProps = {
  scheduledConsult?: Consultation | null;
};

const DEFAULT_FOLLOWUP_VALUE = 8;

export const ConsultNotes = ({ scheduledConsult }: ConsultNotesProps) => {
  const { fields } = useAppStore.use.guidedDoctorConsult();
  const setFieldsState = useAppStore.use.setFieldsState();
  const setIsDirty = useAppStore.use.setIsDirty();
  const { allowAccess } = useAccessControl();

  const emitEvent = useEmitUiEvent();
  const { uiEvent } = useAnalyticsTracking();

  const consultFollowupOptions = [...Array(20).keys()].map((itm) => {
    return { name: `${itm + 1} weeks`, value: itm + 1 };
  });

  useEffect(() => {
    if (scheduledConsult?.notes && !fields.notes) {
      setFieldsState('notes', scheduledConsult.notes);
    }
  }, [scheduledConsult?.notes]);

  const handleSetFollowUpPeriod = (event: SelectChangeEvent<number>) => {
    const value = Number(event?.target.value);
    setFieldsState('followUpWeekPeriod', value);

    emitEvent({
      component_name: 'FollowUpConsultationSelect',
      event_name: 'select_follow_up_consultation',
      id: 'select_follow_up_consultation',
      value: `${value} weeks`,
      context_id: uiEvent?.context_id,
    });
  };

  const onNotesChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldsState('notes', event?.target?.value);
    setIsDirty(true);
  };

  return (
    <Box>
      <TextField
        value={fields.notes}
        onChange={onNotesChanged}
        placeholder="Add detailed notes from the consult, treatment plan and next steps."
        fullWidth
        multiline
        minRows={5}
        variant="outlined"
        aria-label="notes"
      />
      {allowAccess(
        [UserRole.Doctor],
        <Box display="flex" gap={5} mt={4} alignItems="center">
          <Typography>Follow-up Consultation</Typography>
          <Select
            sx={{ width: '10rem' }}
            size="small"
            data-testid="follow-up-period"
            value={fields.followUpWeekPeriod || DEFAULT_FOLLOWUP_VALUE}
            onChange={(event) => handleSetFollowUpPeriod(event)}
          >
            {consultFollowupOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </Box>,
      )}
    </Box>
  );
};
